import React, { useState } from 'react';
import { Phone, Mail, MapPin, Building, User, ChevronDown, ChevronUp, Eye, Paperclip } from 'lucide-react';

const formatAddress = (customer) => {
  const parts = [];
  if (customer.address_line1) parts.push(customer.address_line1);
  if (customer.address_line2) parts.push(customer.address_line2);
  if (customer.address_city) parts.push(customer.address_city);
  if (customer.address_county) parts.push(customer.address_county);
  if (customer.address_postcode) parts.push(customer.address_postcode);
  if (customer.address_country && customer.address_country !== 'United Kingdom') {
    parts.push(customer.address_country);
  }
  return parts.length > 0 ? parts.join(', ') : 'No address provided';
};

const ordinalSuffix = (d) => {
  if (d > 3 && d < 21) return 'th';
  switch (d % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatDateOrdinal = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  const year = d.getFullYear();
  return `${day}${ordinalSuffix(day)} ${month} ${year}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const CustomerSidebar = ({ customer, onComposeEmail, sentEmails = [], onEmailClick }) => {
  const [showEmails, setShowEmails] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow-md p-5 lg:sticky lg:top-4">
      {/* Contact Name, Company & ID */}
      <div className="mb-4">
        <div className="flex items-center gap-2 text-gray-700">
          <User size={15} className="text-gray-400 flex-shrink-0" />
          {customer.first_name || customer.last_name
            ? <span className="font-semibold">{[customer.first_name, customer.last_name].filter(Boolean).join(' ')}</span>
            : <span className="italic text-gray-400">No contact name</span>
          }
        </div>
        {customer.company_name && (
          <div className="flex items-center gap-1.5 text-sm text-gray-600 mt-1">
            <Building size={14} />
            <span>{customer.company_name}</span>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div className="space-y-3 text-sm">
        {customer.phone ? (
          <a href={`tel:${customer.phone}`} className="flex items-center gap-2 text-gray-700 hover:text-pest-blue transition-colors">
            <Phone size={15} className="text-gray-400 flex-shrink-0" />
            <span>{customer.phone}</span>
          </a>
        ) : (
          <div className="flex items-center gap-2 text-gray-400">
            <Phone size={15} className="flex-shrink-0" />
            <span className="italic">No phone</span>
          </div>
        )}
        {customer.email ? (
          <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-gray-700 hover:text-pest-blue transition-colors break-all">
            <Mail size={15} className="text-gray-400 flex-shrink-0" />
            <span>{customer.email}</span>
          </a>
        ) : (
          <div className="flex items-center gap-2 text-gray-400">
            <Mail size={15} className="flex-shrink-0" />
            <span className="italic">No email</span>
          </div>
        )}
        {customer.address_line1 ? (
          <div className="flex items-start gap-2 text-gray-700">
            <MapPin size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <span>{formatAddress(customer)}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-400">
            <MapPin size={15} className="flex-shrink-0" />
            <span className="italic">No address</span>
          </div>
        )}
      </div>

      {/* Notes */}
      {customer.notes && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 text-xs uppercase tracking-wide mb-1">Notes</h4>
          <p className="text-gray-700 text-sm whitespace-pre-wrap">{customer.notes}</p>
        </div>
      )}

      {/* Sent Emails Section */}
      {sentEmails.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <button
            onClick={() => setShowEmails(!showEmails)}
            className="flex items-center justify-between w-full text-left"
          >
            <h4 className="font-medium text-gray-900 text-xs uppercase tracking-wide">
              Sent Emails ({sentEmails.length})
            </h4>
            {showEmails ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
          </button>
          {showEmails && (
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
              {sentEmails.map(email => (
                <div
                  key={email.id}
                  className={`p-2 bg-gray-50 rounded text-xs${onEmailClick ? ' cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                  onClick={onEmailClick ? () => onEmailClick(email) : undefined}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-gray-400">{formatDate(email.date_sent)}</span>
                    <div className="flex items-center gap-1">
                      {email.attachment_count > 0 && (
                        <span className="flex items-center text-gray-400">
                          <Paperclip size={10} />{email.attachment_count}
                        </span>
                      )}
                      {email.read_date && <Eye size={12} className="text-green-500" />}
                    </div>
                  </div>
                  <p className="text-gray-700 font-medium truncate">{email.subject}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Member since & Last activity */}
      <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400 flex justify-between">
        <span>Customer since: {formatDateOrdinal(customer.created_at)}</span>
        {(customer.qb_last_updated_date || customer.updated_at) && (
          <span>Last activity: {formatDateOrdinal(customer.qb_last_updated_date || customer.updated_at)}</span>
        )}
      </div>
    </div>
  );
};

export default CustomerSidebar;
